import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, CalendarCheck, Loader2, X, RotateCcw } from 'lucide-react';
import { format as formatDate } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface MarkAsRereadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bookId: string;
  bookTitle: string;
  onSuccess?: () => void;
}

export function MarkAsRereadDialog({
  isOpen,
  onClose,
  bookId,
  bookTitle,
  onSuccess
}: MarkAsRereadDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rereadAt, setRereadAt] = useState<Date | undefined>(undefined);
  const [calendarMonth, setCalendarMonth] = useState<Date | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setRereadAt(undefined);
      setCalendarMonth(undefined);
      setNotes('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('user_book_rereads')
        .insert({
          user_id: user.id,
          book_id: bookId,
          reread_at: rereadAt ? rereadAt.toISOString().split('T')[0] : null,
          notes: notes.trim() || null
        });

      if (error) throw error;

      toast({
        title: 'Reread logged!',
        description: `You've logged a reread of "${bookTitle}"`,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error logging reread:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not log reread. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-primary" />
            Log a Reread
          </DialogTitle>
          <DialogDescription>
            Record another read of <span className="font-medium text-foreground">{bookTitle}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Finished Reread */}
          <div className="space-y-2">
            <Label>When did you finish this reread?</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !rereadAt && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {rereadAt ? formatDate(rereadAt, "PPP") : "Pick a date (optional)"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={rereadAt}
                    onSelect={setRereadAt}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    initialFocus
                    disabled={(date) => date > new Date()}
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {!rereadAt ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="default"
                  onClick={() => setRereadAt(new Date())}
                  className="shrink-0"
                >
                  <CalendarCheck className="w-4 h-4 mr-1" />
                  Today
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setRereadAt(undefined)}
                  className="shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="reread-notes">Notes (Optional)</Label>
            <Textarea
              id="reread-notes"
              placeholder="Any thoughts about this reread? Did you notice something new?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Log Reread
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
